import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Send, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export const AssignmentChat = ({ enquiryId }) => {
  const { user } = useAuth();
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const scrollRef = useRef();
  const stickRef = useRef(true);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (el) stickRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
  };

  const load = async () => {
    try {
      const { data } = await api.get(`/enquiries/${enquiryId}/chat`);
      setMsgs(data.messages || []);
    } catch (e) { /* ignore */ } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 6000);
    return () => clearInterval(iv);
    // eslint-disable-next-line
  }, [enquiryId]);

  // Keep the chat pinned at the bottom of its own box (never scrolls the page)
  useEffect(() => {
    const el = scrollRef.current;
    if (el && stickRef.current) el.scrollTop = el.scrollHeight;
  }, [msgs]);

  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      await api.post(`/enquiries/${enquiryId}/chat`, { text });
      setText("");
      stickRef.current = true;
      await load();
    } catch (e) { /* ignore */ } finally { setSending(false); }
  };

  return (
    <div className="mt-3 border-t border-brand-line pt-3" data-testid={`assignment-chat-${enquiryId}`}>
      <div className="text-xs font-semibold text-brand-muted mb-2">Work Chat · Admin ↔ Mediator</div>
      <div ref={scrollRef} onScroll={handleScroll} style={{ scrollBehavior: "smooth" }} className="max-h-48 overflow-y-auto space-y-2 mb-2 no-scrollbar">
        {loading ? (
          <div className="flex justify-center py-3"><Loader2 className="w-4 h-4 animate-spin text-brand-emerald" /></div>
        ) : msgs.length === 0 ? (
          <div className="text-xs text-brand-muted text-center py-2">No messages yet. Start the conversation.</div>
        ) : msgs.map((m) => {
          const mine = m.sender_id === user.id;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] px-3 py-1.5 rounded-2xl text-sm ${mine ? "bg-brand-emerald text-white" : "bg-secondary text-brand-ink"}`}>
                <div className="text-[10px] opacity-70 mb-0.5">{m.sender_name}</div>{m.text}
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex gap-2">
        <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Type a message..." className="h-9" data-testid={`assignment-chat-input-${enquiryId}`} />
        <Button size="sm" onClick={send} disabled={sending} className="rounded-full bg-brand-emerald" data-testid={`assignment-chat-send-${enquiryId}`}><Send className="w-4 h-4" /></Button>
      </div>
    </div>
  );
};

export default AssignmentChat;
